import { useState } from 'react'
import { Bell, Mail, MessageSquare, Save, Plus, X, Edit2, Trash2 } from 'lucide-react'

interface AlertSubscription {
  id: string
  name: string
  description: string
  businessIds: string[]
  triggers: AlertTrigger[]
  channels: AlertChannel[]
  frequency: 'immediate' | 'daily' | 'weekly' | 'monthly'
  isActive: boolean
  createdAt: string
}

interface AlertTrigger {
  type: 'score_change' | 'band_change' | 'red_flag' | 'risk_level_change'
  threshold?: number
  conditions: Record<string, any>
}

interface AlertChannel {
  type: 'email' | 'sms' | 'push'
  address: string
  isActive: boolean
}

interface AlertSubscriptionManagerProps {
  subscriptions: AlertSubscription[]
  onSave: (subscriptions: AlertSubscription[]) => void
}

export function AlertSubscriptionManager({ subscriptions, onSave }: AlertSubscriptionManagerProps) {
  const [localSubscriptions, setLocalSubscriptions] = useState<AlertSubscription[]>(subscriptions)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)

  const alertTypes = [
    { value: 'score_change', label: 'Score Changes', description: 'When TLBH score changes by specified threshold' },
    { value: 'band_change', label: 'Band Changes', description: 'When credit band changes (A, B, C, D)' },
    { value: 'red_flag', label: 'Red Flags', description: 'When new red flags are detected' },
    { value: 'risk_level_change', label: 'Risk Level Changes', description: 'When risk assessment level changes' }
  ]

  const channelTypes = [
    { value: 'email', label: 'Email', icon: Mail },
    { value: 'sms', label: 'SMS', icon: MessageSquare },
    { value: 'push', label: 'Push Notification', icon: Bell }
  ]

  const frequencies = [
    { value: 'immediate', label: 'Immediate' },
    { value: 'daily', label: 'Daily Digest' },
    { value: 'weekly', label: 'Weekly Summary' },
    { value: 'monthly', label: 'Monthly Report' }
  ]

  const createNewSubscription = (): AlertSubscription => ({
    id: `sub-${Date.now()}`,
    name: '',
    description: '',
    businessIds: [],
    triggers: [{ type: 'score_change', threshold: 5, conditions: {} }],
    channels: [{ type: 'email', address: '', isActive: true }],
    frequency: 'immediate',
    isActive: true,
    createdAt: new Date().toISOString()
  })

  const handleAddSubscription = () => {
    const newSubscription = createNewSubscription()
    setLocalSubscriptions([...localSubscriptions, newSubscription])
    setEditingId(newSubscription.id)
    setShowCreateForm(true)
  }

  const handleDeleteSubscription = (id: string) => {
    setLocalSubscriptions(localSubscriptions.filter(sub => sub.id !== id))
  }

  const handleToggleSubscription = (id: string) => {
    setLocalSubscriptions(localSubscriptions.map(sub =>
      sub.id === id ? { ...sub, isActive: !sub.isActive } : sub
    ))
  }

  const handleUpdateSubscription = (id: string, updates: Partial<AlertSubscription>) => {
    setLocalSubscriptions(localSubscriptions.map(sub =>
      sub.id === id ? { ...sub, ...updates } : sub
    ))
  }

  const handleSave = () => {
    onSave(localSubscriptions)
    setEditingId(null)
    setShowCreateForm(false)
  }

  const SubscriptionForm = ({ subscription }: { subscription: AlertSubscription }) => (
    <div className="space-y-6">
      {/* Basic Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Subscription Name
          </label>
          <input
            type="text"
            value={subscription.name}
            onChange={(e) => handleUpdateSubscription(subscription.id, { name: e.target.value })}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="e.g., High Risk Alerts"
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Frequency
          </label>
          <select
            value={subscription.frequency}
            onChange={(e) => handleUpdateSubscription(subscription.id, { frequency: e.target.value as any })}
            className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          >
            {frequencies.map(freq => (
              <option key={freq.value} value={freq.value}>
                {freq.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Description
        </label>
        <textarea
          value={subscription.description}
          onChange={(e) => handleUpdateSubscription(subscription.id, { description: e.target.value })}
          rows={2}
          className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          placeholder="Describe what this subscription monitors..."
        />
      </div>

      {/* Alert Triggers */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Alert Triggers
        </h4>
        <div className="space-y-3">
          {alertTypes.map(type => {
            const isSelected = subscription.triggers.some(t => t.type === type.value)
            return (
              <div key={type.value} className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => {
                    const triggers = e.target.checked
                      ? [...subscription.triggers, { type: type.value as any, conditions: {} }]
                      : subscription.triggers.filter(t => t.type !== type.value)
                    handleUpdateSubscription(subscription.id, { triggers })
                  }}
                  className="mt-1"
                />
                <div className="flex-1">
                  <label className="text-sm font-medium text-gray-900 dark:text-white">
                    {type.label}
                  </label>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {type.description}
                  </p>
                  {isSelected && type.value === 'score_change' && (
                    <div className="mt-2">
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                        Minimum Score Change Threshold
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="100"
                        value={subscription.triggers.find(t => t.type === 'score_change')?.threshold || 5}
                        onChange={(e) => {
                          const triggers = subscription.triggers.map(t =>
                            t.type === 'score_change' ? { ...t, threshold: parseInt(e.target.value) } : t
                          )
                          handleUpdateSubscription(subscription.id, { triggers })
                        }}
                        className="w-20 border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">points</span>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Notification Channels */}
      <div>
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Notification Channels
        </h4>
        <div className="space-y-3">
          {subscription.channels.map((channel, index) => {
            const ChannelIcon = channelTypes.find(ct => ct.value === channel.type)?.icon || Bell
            return (
              <div key={index} className="flex items-center space-x-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                <ChannelIcon className="w-5 h-5 text-gray-400" />
                <select
                  value={channel.type}
                  onChange={(e) => {
                    const channels = [...subscription.channels]
                    channels[index] = { ...channels[index], type: e.target.value as any }
                    handleUpdateSubscription(subscription.id, { channels })
                  }}
                  className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {channelTypes.map(ct => (
                    <option key={ct.value} value={ct.value}>
                      {ct.label}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={channel.address}
                  onChange={(e) => {
                    const channels = [...subscription.channels]
                    channels[index] = { ...channels[index], address: e.target.value }
                    handleUpdateSubscription(subscription.id, { channels })
                  }}
                  className="flex-1 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder={
                    channel.type === 'email' ? 'email@example.com' :
                    channel.type === 'sms' ? '+91-9876543210' : 'Device ID'
                  }
                />
                <button
                  onClick={() => {
                    const channels = subscription.channels.filter((_, i) => i !== index)
                    handleUpdateSubscription(subscription.id, { channels })
                  }}
                  className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )
          })}
          <button
            onClick={() => {
              const channels = [...subscription.channels, { type: 'email', address: '', isActive: true }]
              handleUpdateSubscription(subscription.id, { channels })
            }}
            className="inline-flex items-center px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Channel
          </button>
        </div>
      </div>
    </div>
  )

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            Alert Subscriptions
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Manage your alert preferences and notification settings
          </p>
        </div>
        
        <button
          onClick={handleAddSubscription}
          className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Subscription
        </button>
      </div>

      {/* Subscriptions List */}
      <div className="space-y-4">
        {localSubscriptions.map((subscription) => (
          <div key={subscription.id} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            {editingId === subscription.id ? (
              <div className="p-6">
                <SubscriptionForm subscription={subscription} />
                <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => {
                      setEditingId(null)
                      setShowCreateForm(false)
                    }}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-md text-sm font-medium"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </button>
                </div>
              </div>
            ) : (
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        {subscription.name || 'Unnamed Subscription'}
                      </h3>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        subscription.isActive
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300'
                      }`}>
                        {subscription.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 dark:text-gray-400 mb-3">
                      {subscription.description || 'No description provided'}
                    </p>
                    
                    <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                      <span>Frequency: {frequencies.find(f => f.value === subscription.frequency)?.label}</span>
                      <span>Triggers: {subscription.triggers.length}</span>
                      <span>Channels: {subscription.channels.length}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleToggleSubscription(subscription.id)}
                      className={`p-2 rounded-md ${
                        subscription.isActive
                          ? 'text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20'
                          : 'text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      <Bell className="w-4 h-4" />
                    </button>
                    
                    <button
                      onClick={() => setEditingId(subscription.id)}
                      className="p-2 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    
                    <button
                      onClick={() => handleDeleteSubscription(subscription.id)}
                      className="p-2 rounded-md text-red-400 hover:text-red-600 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
        
        {localSubscriptions.length === 0 && (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Alert Subscriptions
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Create your first alert subscription to stay informed about business changes.
            </p>
            <button
              onClick={handleAddSubscription}
              className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Subscription
            </button>
          </div>
        )}
      </div>
    </div>
  )
}